// Layer 3 — annual value projections from the NSR and Capacity engines.

import type { CapacitySeed } from '@/versions/v1/schema/capacity';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { CAPACITY_SEED } from '@/versions/v1/shared/capacity-seed';
import { PLANNING_MONTH_PROFILE, yearOneScheduledHours } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { capacityAtReference } from '@/versions/v1/shared/CapacityEngine';
import { nsrRead } from '@/versions/v1/shared/NsrEngine';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { downtimeEvents } from '@/versions/v1/modules/maintenance/DowntimeEvents.transform';
import { downtimeImpactRead, uptimeOpportunityRead } from '@/versions/v1/modules/downtime/DowntimeImpact.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

export const AVAILABILITY_UPLIFT_POINTS = 1;

type MetalRecovery = { pb: number; zn: number; ag: number };

function recoveryNumbers(state: SessionState): {
  actual: MetalRecovery;
  design: MetalRecovery;
} {
  const values = readingsAt(state);
  const value = (id: string) => {
    const result = values.find((reading) => reading.id === id)?.value;
    if (typeof result !== 'number') throw new Error(`ValueBand: missing recovery reading ${id}`);
    return result;
  };
  const designValue = (id: string) => {
    const result = readingByIdLookup(id)?.design;
    if (typeof result !== 'number') throw new Error(`ValueBand: missing design recovery ${id}`);
    return result;
  };
  return {
    actual: {
      pb: value('rd-kpi-pb-rec'),
      zn: value('rd-kpi-zn-rec'),
      ag: value('rd-kpi-ag-rec'),
    },
    design: {
      pb: designValue('rd-kpi-pb-rec'),
      zn: designValue('rd-kpi-zn-rec'),
      ag: designValue('rd-kpi-ag-rec'),
    },
  };
}

export type AvailabilityUpliftRead = {
  annualA: number;
  scheduledHours: number;
  additionalHours: number;
  additionalTonnes: number;
  currentThroughputStph: number;
  currentGrade: CapacitySeed['grade'];
  dailyEbitdaA: number;
  availabilityPct: number;
};

/**
 * Value of one availability point on the Year 1 scheduled-hour basis. The
 * `/downtime` uptime tile renders the same `uptimeOpportunityRead` (D12), so the
 * landing and the drill-down cannot disagree.
 */
export function availabilityUpliftAnnualValue(
  availabilityPoints = AVAILABILITY_UPLIFT_POINTS,
): AvailabilityUpliftRead {
  const uptime = uptimeOpportunityRead(availabilityPoints);
  const monthFour = PLANNING_MONTH_PROFILE.find((month) => month.id === 'm4');
  return {
    annualA: uptime.annualEbitdaA,
    scheduledHours: uptime.yearOneScheduledHours,
    additionalHours: uptime.additionalHours,
    additionalTonnes: uptime.additionalFeedShortTons,
    currentThroughputStph: uptime.throughputStph,
    currentGrade: uptime.grade,
    dailyEbitdaA: uptime.currentEbitdaPerHourA * 24,
    availabilityPct: monthFour?.actualMillAvailabilityPct ?? 0,
  };
}

export type ValueBandRead = {
  recoveryGapAnnualA: number;
  recoveryPointAnnualA: number;
  recoveryBasis: string;
  recoveryZnActualPct: number;
  recoveryZnDesignPct: number;
  recoveryPbActualPct: number;
  recoveryPbDesignPct: number;
  throughputAnnualA: number;
  throughputHeadroomStph: number;
  throughputCurrentStph: number;
  throughputCeilingStph: number;
  throughputYear1ScheduledHours: number;
  uptimeAnnualA: number;
  uptimeOnePointHours: number;
  uptimeAvailabilityPct: number;
  uptimeYear1ScheduledHours: number;
  uptimeYtdUnplannedHours: number;
  uptimeYtdUnplannedCostA: number;
  recoveryHref: string;
  throughputHref: string;
  uptimeHref: string;
};

export function valueBandRead(
  state: SessionState,
  projectId = 'alpha',
): ValueBandRead {
  const { actual, design } = recoveryNumbers(state);
  const recoveryOnly = nsrRead(state, undefined, {
    actual: { pb: actual.pb, zn: actual.zn, ag: design.ag },
  });
  const oneZnPoint = nsrRead(state, undefined, {
    actual: { pb: actual.pb, zn: actual.zn + 1, ag: design.ag },
  });
  const capacity = capacityAtReference(CAPACITY_SEED);
  const scheduledHours = yearOneScheduledHours();
  const uptime = availabilityUpliftAnnualValue(AVAILABILITY_UPLIFT_POINTS);
  // The comparison is the `/downtime` Year 1 impact read: lost mill hours after
  // buffer, priced at the same EBITDA per operating hour.
  const yearToDate = downtimeImpactRead(downtimeEvents(), 'ytd');

  return {
    recoveryGapAnnualA: (recoveryOnly.budgetDaily - recoveryOnly.daily.total) * (scheduledHours / 24),
    recoveryPointAnnualA: (oneZnPoint.daily.total - recoveryOnly.daily.total) * (scheduledHours / 24),
    recoveryBasis: 'PFS design',
    recoveryZnActualPct: actual.zn,
    recoveryZnDesignPct: design.zn,
    recoveryPbActualPct: actual.pb,
    recoveryPbDesignPct: design.pb,
    throughputAnnualA: capacity.step1.annualisedEbitdaGainA,
    throughputHeadroomStph: capacity.headroomStph,
    throughputCurrentStph: capacity.currentThroughputStph,
    throughputCeilingStph: capacity.maxAchievableStph,
    throughputYear1ScheduledHours: scheduledHours,
    uptimeAnnualA: uptime.annualA,
    uptimeOnePointHours: uptime.additionalHours,
    uptimeAvailabilityPct: uptime.availabilityPct,
    uptimeYear1ScheduledHours: uptime.scheduledHours,
    uptimeYtdUnplannedHours: yearToDate.lostHours,
    uptimeYtdUnplannedCostA: yearToDate.valueLostA,
    recoveryHref: `/projects/${projectId}/advisory/cases?outcome=recovery`,
    throughputHref: `/projects/${projectId}/capacity`,
    uptimeHref: `/projects/${projectId}/downtime`,
  };
}

export function formatAmountA(amount: number): string {
  const absolute = Math.abs(amount);
  if (absolute >= 1_000_000) return `${CURRENCY_GLYPH}${(absolute / 1_000_000).toFixed(1)}M`;
  if (absolute >= 10_000) return `${CURRENCY_GLYPH}${Math.round(absolute / 1_000)}K`;
  if (absolute >= 1_000) return `${CURRENCY_GLYPH}${(absolute / 1_000).toFixed(1)}K`;
  return `${CURRENCY_GLYPH}${Math.round(absolute).toLocaleString('en-AU')}`;
}

export function formatAnnualA(amount: number): string {
  const sign = amount < 0 ? '−' : '';
  return `${sign}${formatAmountA(amount)}/yr`;
}

export function formatDailyA(amount: number): string {
  const sign = amount < 0 ? '−' : '';
  return `${sign}${formatAmountA(amount)}/day`;
}

export type ValueBandTileId = 'recovery' | 'throughput' | 'uptime';

/**
 * The band's headline figure, per tile (#731). A seat that reads money sees the
 * annual US$; a floor seat (Operator, Control room) reads the tile's own
 * non-money basis instead — the Zn points below design, the stph of headroom,
 * the hours one availability point is worth — so no slot is left empty.
 */
export function valueBandTileValue(
  read: ValueBandRead,
  tile: ValueBandTileId,
  showMoney: boolean,
): string {
  if (showMoney) {
    const annual = tile === 'recovery'
      ? read.recoveryGapAnnualA
      : tile === 'throughput' ? read.throughputAnnualA : read.uptimeAnnualA;
    return formatAnnualA(annual);
  }
  if (tile === 'recovery') return `${recoveryZnGapPt(read).toFixed(1)} pt below design`;
  if (tile === 'throughput') return `${read.throughputHeadroomStph.toFixed(1)} stph`;
  return `${Math.round(read.uptimeOnePointHours)} h`;
}

/**
 * The band's secondary comparison, per tile (#731). A floor seat keeps the
 * operating basis and drops the priced tail: `+1 Zn point` without its annual
 * US$; `163.5 h unplanned` without the Year 1 cost.
 */
export function valueBandTileComparison(
  read: ValueBandRead,
  tile: ValueBandTileId,
  showMoney: boolean,
): string {
  if (tile === 'recovery') {
    return showMoney ? `+1 Zn point → ${formatAnnualA(read.recoveryPointAnnualA)}` : '+1 Zn point';
  }
  if (tile === 'throughput') {
    return showMoney
      ? `${read.throughputHeadroomStph.toFixed(1)} stph available · ${read.throughputYear1ScheduledHours.toLocaleString('en-AU')} scheduled h in Year 1`
      : `${read.throughputYear1ScheduledHours.toLocaleString('en-AU')} scheduled h in Year 1`;
  }
  return showMoney
    ? `${read.uptimeYtdUnplannedHours.toFixed(1)} h unplanned · ${formatAmountA(read.uptimeYtdUnplannedCostA)} cost Year 1 to date`
    : `${read.uptimeYtdUnplannedHours.toFixed(1)} h unplanned`;
}

/** The Zn recovery gap below PFS design, in percentage points (`85.1 − 82.3 = 2.8`). */
export function recoveryZnGapPt(read: ValueBandRead): number {
  return read.recoveryZnDesignPct - read.recoveryZnActualPct;
}
